import { isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID, signal, WritableSignal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, interval, Subscription, switchMap, take, takeWhile, tap } from 'rxjs';
import {
  IExecuteOrder,
  IMethod,
  IPaymentConfirmationResult,
  IPaymentErrorResult,
  IPaymentInitData,
  IPaymentResultMethods,
  IPaymentStatus,
  IPaymentSuccessResult,
  PaymentProvider,
  PaymentStatus
} from '../../models';
import { LocalStorageService } from '../localstorage.service';
import { MethodsService } from '../methods/methods.service';
import { SystemService } from '../system.service';
import { ToastService } from '../toast.service';
import { BasicPaymentProvider } from './payment-providers/basic.pp';
import { DatatransPaymentProvider } from './payment-providers/datatrans/datatrans.pp';
import { PaypalPaymentProvider } from './payment-providers/paypal/paypal.pp';
import { StripePaymentProvider } from './payment-providers/stripe/stripe.pp';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  // Services
  public readonly localStorageService = inject(LocalStorageService);
  private readonly datatransPaymentProvider = inject(DatatransPaymentProvider);
  private readonly methodsService = inject(MethodsService);
  private readonly paypalPaymentProvider = inject(PaypalPaymentProvider);
  private readonly stripePaymentProvider = inject(StripePaymentProvider);
  private readonly systemService = inject(SystemService);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly platformId = inject(PLATFORM_ID);

  // Properties
  /** Indicates if a payment is currently being processed */
  public processingPayment: WritableSignal<boolean> = signal(false);
  /** The latest status of the latest payment */
  public status: WritableSignal<IPaymentStatus | undefined> = signal(undefined);
  /** Emits every time a new result gets provided by any paymentProvider */
  public resultReceived: BehaviorSubject<IPaymentStatus | undefined> = new BehaviorSubject<IPaymentStatus | undefined>(undefined);
  /** All payment-providers listed with their key and service */
  private readonly _paymentProviders: Record<PaymentProvider, BasicPaymentProvider> = {
    DataTransServiceProvider: this.datatransPaymentProvider,
    PayPalCheckoutProvider2: this.paypalPaymentProvider,
    StripeServiceProvider: this.stripePaymentProvider
  };
  /** All subscriptions to unsubscribe on garbage collection */
  private subscriptions: Subscription[] = [];
  /**
   * Result methods delivered by the freshly loaded checkout page (only present when the page was called with ?orderID).
   * They belong to the order the provider redirected back for and take precedence over the localStorage copy.
   */
  private pageResultMethods: IPaymentResultMethods | undefined;
  /** Clears the processing state when the tab is restored from the bfcache (user aborted at the provider) */
  private readonly onPageShow = (event: PageTransitionEvent): void => {
    if (event.persisted) this.processingPayment.set(false);
  };

  // Setters
  private set resultMethods(methods: IPaymentResultMethods) {
    this.localStorageService.set('paymentResultMethods', methods);
  }

  // Getters
  private get resultMethods(): IPaymentResultMethods {
    return this.pageResultMethods ?? this.localStorageService.get('paymentResultMethods');
  }

  /**
   * Initialize a new Payment with the given initData from the backend "createorder"-endpoint
   * @param initData to use for the payment provider (including the transactionToken)
   */
  public initializePayment(initData: IPaymentInitData, resultMethods: IPaymentResultMethods): void {
    this.processingPayment.set(true);
    // A new order was created: the methods of the page belong to the previous one
    this.pageResultMethods = undefined;
    this.resultMethods = resultMethods;
    const provider = this._paymentProviders[initData.provider];
    provider.initializePayment(initData.transactionToken, initData.redirectUrl);
  }

  /**
   * Initialize all payment providers
   * @param pageResultMethods result methods of the checkout page, used after the redirect back from the provider.
   * The localStorage copy from "createorder" is only the fallback, so the redirect also works in another browser.
   */
  public initializeProviders(pageResultMethods?: IPaymentResultMethods): void {
    this.pageResultMethods = this.hasResultMethods(pageResultMethods) ? pageResultMethods : undefined;
    // TODO: Initialize only the providers wich can get used by the user
    Object.values(this._paymentProviders).forEach((provider) => {
      console.debug('[PaymentService] Initializing provider: ', provider);
      this.subscriptions.push(
        provider.resultReceived.subscribe((result) => {
          console.debug('[PaymentService] Received payment result: ', result);
          if (result) {
            this.status?.set(result);
            this.handlePaymentResult(result, provider);
          }
        })
      );
      provider.initializeProvider();
    });
    // Providers leave the page via window.open(url, '_self'). Going back with the browser button restores
    // this tab from the bfcache with processingPayment still set, so the overlay would never disappear.
    if (isPlatformBrowser(this.platformId)) window.addEventListener('pageshow', this.onPageShow);
  }

  /**
   * Will clean up every garbage from the payment-service
   * @example unsubscribe all subscriptions
   */
  public garbageCollection(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions = [];
    if (isPlatformBrowser(this.platformId)) window.removeEventListener('pageshow', this.onPageShow);
    this.pageResultMethods = undefined;
    this.localStorageService.remove('paymentResultMethods');
  }

  /**
   * Checks whether any result method is set — the checkout page only delivers them for a given orderID
   * @param methods to check
   */
  private hasResultMethods(methods?: IPaymentResultMethods): methods is IPaymentResultMethods {
    return !!(methods?.paymentSuccess || methods?.paymentCancel || methods?.paymentError || methods?.paymentStatus);
  }

  /**
   * Executes the order by calling the given method
   * @param method to call for executing the order
   */
  public executeOrder(method: IMethod): void {
    this.processingPayment.set(true);
    this.methodsService.callMethod<IExecuteOrder>(method).subscribe({
      next: (executeOrderResult) => {
        if (executeOrderResult?.checkoutSucceededURL) {
          this.systemService.openLink(executeOrderResult.checkoutSucceededURL);
        } else {
          // No success page means no navigation to end the loading state — without this the
          // checkout would stay on "payment is being processed" although the order went through.
          this.processingPayment.set(false);
        }
        this.toastService.showToast(
          this.translateService.instant('paymentService.success'),
          executeOrderResult?.order?.orderNumber
            ? this.translateService.instant('paymentService.orderNumber') + executeOrderResult.order.orderNumber
            : undefined,
          'success'
        );
      },
      error: (err) => {
        this.processingPayment.set(false);
        this.toastService.showToast(this.translateService.instant('paymentService.error'), 'error');
        console.error('Failed to execute the order: ', err);
      }
    });
  }

  /**
   * Handles any payment result and calls the corresponding method
   * @param status of the payment
   */
  private handlePaymentResult(status: IPaymentStatus, provider: BasicPaymentProvider): void {
    this.processingPayment.set(status.status === PaymentStatus.Pending);
    this.resultReceived.next(status);
    if (!this.resultMethods) {
      console.debug('[PaymentService] No result methods provided to handle the payment result for: ', status);
      return;
    }
    const PAYMENT_CHECK_INTERVAL_MS = 5000; // Check every 5 seconds
    const PAYMENT_CHECK_TIMEOUT_MIN = 2; // Timeout after 2 minutes
    const MAX_POLL_ATTEMPTS = (PAYMENT_CHECK_TIMEOUT_MIN * 60 * 1000) / PAYMENT_CHECK_INTERVAL_MS; // Total number of polling attempts within the timeout
    switch (status.status) {
      case PaymentStatus.Pending: {
        // Start polling to check payment confirmation
        let paymentStatus: PaymentStatus | undefined;
        interval(PAYMENT_CHECK_INTERVAL_MS)
          .pipe(
            take(MAX_POLL_ATTEMPTS),
            switchMap(() => {
              if (!this.resultMethods.paymentStatus) throw new Error('No payment status method provided');
              return this.methodsService
                .callMethod<IPaymentConfirmationResult>(this.resultMethods.paymentStatus, { transactionToken: status.transactionId })
                .pipe(
                  tap(() => {
                    status.message = provider.generateStatusMessage(status);
                  })
                );
            }),
            takeWhile((paymentConfirmationResult) => {
              // Continue polling while payment is not completed
              return paymentConfirmationResult === undefined || paymentConfirmationResult?.paymentStatus === PaymentStatus.Pending;
            }, true) // inclusive: true emits the value that makes the condition false
          )
          .subscribe({
            next: (paymentConfirmationResult) => {
              if (!paymentConfirmationResult) return;
              if (paymentConfirmationResult.paymentStatus === PaymentStatus.Pending) {
                console.debug('[PaymentService] Payment pending, continuing to poll...');
                return;
              }
              // Payment completed, emit status
              paymentStatus = paymentConfirmationResult.paymentStatus;
              const resolved: IPaymentStatus = { ...status, status: paymentConfirmationResult.paymentStatus };

              if (paymentConfirmationResult.paymentStatus === PaymentStatus.Succeeded) {
                // Publish directly instead of through provider.resultReceived: that stream feeds
                // handlePaymentResult again, which would run the Succeeded branch on top and
                // execute the very same order a second time.
                this.status.set(resolved);
                this.resultReceived.next(resolved);
                if (paymentConfirmationResult.methods?.executeOrder) {
                  this.executeOrder(paymentConfirmationResult.methods.executeOrder);
                } else {
                  // Paid, but the backend gave us no way to finalize — do not spin forever.
                  this.processingPayment.set(false);
                  this.toastService.showToast(
                    this.translateService.instant('paymentService.errorTitle'),
                    this.translateService.instant('paymentService.confirmationError'),
                    'error',
                    30000
                  );
                }
                return;
              }

              // Cancelled/Failed: hand back to the provider stream so the matching backend
              // method runs and the processing state is cleared.
              if (provider.resultReceived instanceof BehaviorSubject) {
                provider.resultReceived.next(resolved);
              } else {
                this.processingPayment.set(false);
              }
            },
            error: (err) => {
              console.error('Error while polling for payment completion: ', err);
              this.processingPayment.set(false);
              this.toastService.showToast(
                this.translateService.instant('paymentService.errorTitle'),
                this.translateService.instant('paymentService.confirmationError'),
                'error',
                30000
              );
            },
            complete: () => {
              if (paymentStatus === undefined || paymentStatus === PaymentStatus.Pending) {
                this.processingPayment.set(false);
                this.toastService.showToast(
                  this.translateService.instant('paymentService.timeoutTitle'),
                  this.translateService.instant('paymentService.confirmationTimeout'),
                  'error',
                  30000
                );
              }
            }
          });
        break;
      }
      case PaymentStatus.Succeeded:
        this.methodsService
          .callMethod<IPaymentSuccessResult>(this.resultMethods.paymentSuccess, {
            transactionToken: status.transactionId
          })
          .pipe(
            tap(() => {
              status.message = provider.generateStatusMessage(status);
            })
          )
          .subscribe({
            next: (paymentSuccessResult) => {
              if (!paymentSuccessResult) return;
              this.executeOrder(paymentSuccessResult.methods.executeOrder);
            }
          });
        break;
      case PaymentStatus.Cancelled:
        this.methodsService
          .callMethod<void>(this.resultMethods.paymentCancel, {
            transactionToken: status.transactionId
          })
          .pipe(
            tap(() => {
              status.message = provider.generateStatusMessage(status);
            })
          )
          .subscribe();
        break;
      case PaymentStatus.Failed:
        this.methodsService
          .callMethod<IPaymentErrorResult<unknown>>(this.resultMethods.paymentError, {
            transactionToken: status.transactionId
          })
          .pipe(
            tap((errorResult) => {
              status.message = provider.generateStatusMessage(status, errorResult);
            })
          )
          .subscribe();
        break;
    }
  }
}
